import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import PhoneInputWithCountrySelect from 'react-phone-number-input';
import { CREATE_SCHEDULE_LINK } from '~/services/schedule';

interface ScheduleLinkGeneratorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  agentId: string;
  onLinkGenerated?: (link: string) => void;
  calendar_rulers_id?: string;
  scheduleTitle?: string;
}

const ScheduleLinkGeneratorDialog: React.FC<ScheduleLinkGeneratorDialogProps> = ({
  isOpen,
  onClose,
  agentId,
  onLinkGenerated,
  calendar_rulers_id,
  scheduleTitle,
}) => {
  const [t] = useTranslation('schedule');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [name, setName] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [isLinkGenerated, setIsLinkGenerated] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  const [createScheduleLink, { loading: createLinkLoading }] = useMutation(CREATE_SCHEDULE_LINK);

   
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.showModal();
    } else if (!isOpen && dialogRef.current) {
      dialogRef.current.close();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setPhoneNumber('');
      setName('');
      setGeneratedLink('');
      setIsLinkGenerated(false);
      setError('');
      setSuccessMessage('');
      setIsGenerating(false);
    }
  }, [isOpen]);

  const validatePhoneNumber = (phone: string): boolean => {
     if (!phone) return false;
    // Extraer solo los dígitos del número (sin el código de país)
    const cleanPhone = phone.replace('+', '').replace(/\D/g, '');
    return cleanPhone.length >= 10;
  };

  const formatPhoneNumber = (phone: string): string => {
    if (!phone) return '';
    // Si ya tiene formato internacional, devolverlo sin el '+'
    if (phone.startsWith('+')) {
      return phone.replace('+', '');
    }
    // Si no tiene formato internacional, agregar 57 (sin '+')
    const cleanPhone = phone.replace(/\D/g, '');
    return cleanPhone;
  };

  const handleGenerateLink = async () => {
    setError('');
    setIsGenerating(true);
    
    try {
      if (!name || !name.trim()) {
        setError(t('schedule_name_required'));
        setIsGenerating(false);
        return;
      }
      if (!phoneNumber || !phoneNumber.trim()) {
        setError(t('phone_number_required'));
        setIsGenerating(false);
        return;
      }
      if (!validatePhoneNumber(phoneNumber)) {
        setError(t('phone_validation_error'));
        setIsGenerating(false);
        return;
      }

      const formattedPhone = formatPhoneNumber(phoneNumber);

      const { data } = await createScheduleLink({
        variables: {
          data: {
            agent_id: agentId,
            phone_number: formattedPhone,
            name: name,
            ...(calendar_rulers_id && { calendar_rulers_id: calendar_rulers_id })
          }
        }
      });


      // Check if backend call was successful
      if (data?.create_schedule_link?.hasError) {
        setError(t('link_generation_error'));
        setIsGenerating(false);
        return;
      }
      // Generate the frontend link
      const baseUrl = window.location.origin;
      // Use the newly created schedule ID from the response
      const newScheduleId = data.create_schedule_link.data.schedule._id;
      const link = `${baseUrl}/calendar/${newScheduleId}`;
      
      setGeneratedLink(link);
      setIsLinkGenerated(true);
      setSuccessMessage(t('link_generated'));
      
      if (onLinkGenerated) {
        onLinkGenerated(link);
      }
      
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

    } catch (err: any) {
      setError(t('link_generation_error'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setSuccessMessage(t('general_link_copied'));
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      setError(t('link_copy_error'));
      setTimeout(() => {
        setError('');
      }, 3000);
    }
  };

  const handleClose = () => {
    setPhoneNumber('');
    setName('');
    setGeneratedLink('');
    setIsLinkGenerated(false);
    setError('');
    setSuccessMessage('');
    setIsGenerating(false);
    onClose();
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.target.value);
    setError('');
  };

  const handleDialogClick = (e: React.MouseEvent) => {
    if (e.target === dialogRef.current) {
      handleClose();
    }
  };

  return (
    <dialog 
      ref={dialogRef} 
      className="modal"
      onClick={handleDialogClick}
    >
      <div className="modal-box w-full max-w-lg card-brand">
        <div className="flex items-center justify-between mb-6">            <h3 className="text-brand-heading text-lg flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-brand-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            {calendar_rulers_id ? t('specific_schedule_link_title') : t('phone_number_dialog_title')}
          </h3>
          <button
            onClick={handleClose}
            className="btn btn-ghost btn-circle"
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="space-y-4">
          {calendar_rulers_id && scheduleTitle && (
            <div className="alert alert-info bg-blue-50 border-blue-200">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <div>
                <p className="text-blue-800 font-medium">{t('specific_schedule_notice')} <strong>{scheduleTitle}</strong> </p>
               </div>
            </div>
          )}
          <div>
            <p className="text-brand-caption mb-4">
              {calendar_rulers_id ? t('specific_schedule_description') : t('phone_number_description')}
            </p>

            <label className="label">
              <span className="label-text font-medium text-brand-body">
                {t('schedule_name_label')}
              </span>
            </label>
            <input
              type="text"
              placeholder={t('schedule_name_placeholder')}
              className={`input input-brand w-full transition-all ${error.includes('Name') ? 'input-error border-brand-coral' : ''}`}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError('');
              }}
            />

            <label className="label mt-4">
              <span className="label-text font-medium text-brand-body">
                {t('phone_number_required')}
              </span>
            </label>
            <div className="relative">
              <PhoneInputWithCountrySelect
                value={phoneNumber}
                defaultCountry="CO"
                className={`input input-brand w-full transition-all PhoneInputInput ${error ? 'input-error border-brand-coral' : ''}`}
                onChange={(value) => {
                  setPhoneNumber(value || '');
                  setError('');
                }}
                countrySelectProps={{
                  className: 'PhoneInputCountrySelect'
                }}
                international={true}
                withCountryCallingCode={true}
                countryCallingCodeEditable={false}
                displayInitialValueAsLocalNumber={false}
                smartCaret={false}
              />
            </div>
            <label className="label">
              <span className="label-text-alt text-brand-caption">
                Ejemplo: 3001234567
              </span>
            </label>
           
            
          </div>
          {isLinkGenerated && (
            <div className="bg-brand-accent p-4 rounded-lg">
              <label className="label">
                <span className="label-text font-medium text-brand-body">
                  {calendar_rulers_id ? t('specific_schedule_link_generated') : t('general_link_generated')}:
                </span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={generatedLink}
                  readOnly
                  className="input input-brand flex-1 text-sm"
                />
                <button
                  onClick={handleCopyLink}
                  className="btn btn-primary btn-sm rounded-full"
                  title={t('copy_link')}
                  type="button"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          {error && (
            <div className="alert alert-brand-error">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}
          {successMessage && (
            <div className="alert alert-brand-success">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{successMessage}</span>
            </div>
          )}
        </div>
        <div className="modal-action flex gap-2 mt-6">
          <button
            onClick={handleClose}
            className="btn btn-secondary rounded-full"
            disabled={isGenerating || createLinkLoading}
            type="button"
          >
            {t('cancel')}
          </button>
          {!isLinkGenerated ? (
            <button
              onClick={() => {
                handleGenerateLink();
              }}
              className="btn btn-primary rounded-full"
              disabled={!phoneNumber || !phoneNumber.trim() || !name || !name.trim() || isGenerating || createLinkLoading}
              type="button"
            >
              {isGenerating || createLinkLoading ? (
                <span className="loading loading-spinner loading-sm mr-2"></span>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              )}
              {isGenerating || createLinkLoading ? t('generating_link') : t('generate_link')}
            </button>
          ) : (
            <button
              onClick={handleCopyLink}
              className="btn btn-primary rounded-full"
              type="button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              {t('copy_link')}
            </button>
          )}
        </div>
      </div>
    </dialog>
  );
};

export default ScheduleLinkGeneratorDialog;
